import React from "react";
import { makeClassName } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	DateTimeField,
	FormType,
	SubFormField,
	SubFormRenderProps,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { PostDetailQuery } from "../../../Utilities";
import { EditSharingMenu, NestedCreateSharingMenu } from "../../Components/SharingPolicy";
import { MediaAttachmentFormValues, PostFormValues, SharingPolicyCustomFormValues } from "../../FormUtilities";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFieldProps } from "../shared";
import { UserSelectField } from "../User";
import styles from "./field.module.css";

/**
 * Generic props for fields of the Post model.
 */
type FieldProps<K extends keyof PostFormValues.Base = keyof PostFormValues.Base> = BaseFieldProps<
	Pick<PostFormValues.Base, K>
>;

/**
 * Generic props for fields of the Post model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof PostFormValues.Create = keyof PostFormValues.Create> = BaseFieldProps<
	Pick<PostFormValues.Create, K>
>;

/**
 * Generic props for fields of the Post model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof PostFormValues.Detail = keyof PostFormValues.Detail> = BaseFieldProps<
	Pick<PostFormValues.Detail, K>
>;

/**
 * Field component for the `createdAt` field of the Post model.
 */
export function CreatedAt({ formState, ...props }: DetailFieldProps<"createdAt">) {
	return (
		<DateTimeField
			formState={formState}
			name="createdAt"
			optional={FormType.isCreate(formState.formType)}
			precision="minute"
			{...props}
		/>
	);
}

/**
 * Field component for the `title` field of the Post model.
 */
export function Title({ formState, noOptional = true, className, ...props }: FieldProps<"title">) {
	return (
		<TextField
			formState={formState}
			name="title"
			optional
			noOptional={noOptional}
			className={makeClassName(styles["title"], className)}
			{...props}
		/>
	);
}

/**
 * Field component for the `body` field of the Post model.
 */
export function Body({ formState, noHeader = true, ...props }: FieldProps<"body">) {
	return (
		<TextField formState={formState} name="body" multiLine optional noHeader={noHeader} Input={BodyInput} {...props} />
	);
}

function BodyInput(props: TextFieldInputProps) {
	return <TextAreaInput minRows={4} {...props} />;
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<PostFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the Post model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace MediaAttachments {
	/**
	 * SubForm field component for the `mediaAttachments` edge of the TestModel model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"mediaAttachments">) {
		return (
			<MediaAttachmentCustomForm.SubForm
				formState={formState}
				name="mediaAttachments"
				blankItem={MediaAttachmentFormValues.blankItemInPost}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</MediaAttachmentCustomForm.SubForm>
		);
	}

	SubForm.SubForm = function (props: SubFormRenderProps<MediaAttachmentFormValues.CreateInPost>) {
		return (
			<MediaAttachmentCustomForm.SubForm.SubForm {...props}>
				<MediaAttachmentCustomForm.CreateFieldsInPost formState={props.formState} />
			</MediaAttachmentCustomForm.SubForm.SubForm>
		);
	};

	SubForm.AddButton = function (
		props: AddSubFormButtonRenderProps<
			Pick<PostFormValues.Create, "mediaAttachments">,
			MediaAttachmentFormValues.CreateInPost
		>,
	) {
		return <MediaAttachmentCustomForm.SubForm.AddButton {...props} />;
	};
}

export namespace SharingPolicy {
	/**
	 * SubForm field component for the `sharingPolicy` edge of the Post model.
	 */
	export function SubForm({ formState, noHeader = true, className, ...props }: CreateFieldProps<"sharingPolicy">) {
		return (
			<SubFormField
				formState={formState}
				name="sharingPolicy"
				blankItem={SharingPolicyCustomFormValues.blankItemInParent}
				minItems={1}
				maxItems={1}
				formattedBlank=""
				noHeader={noHeader}
				className={makeClassName("sharing-record-sub-form-field", className)}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({ formState }: SubFormRenderProps<SharingPolicyCustomFormValues.CreateInParent>) {
		return <NestedCreateSharingMenu formState={formState} />;
	};

	SubForm.AddButton = function (
		_: AddSubFormButtonRenderProps<
			Pick<PostFormValues.Create, "sharingPolicy">,
			SharingPolicyCustomFormValues.CreateInParent
		>,
	) {
		return <React.Fragment></React.Fragment>;
	};

	export type DetailProps = {
		sharingPolicy: PostDetailQuery["post"]["sharingPolicy"];
	};

	export function Detail({ sharingPolicy }: DetailProps) {
		if (!sharingPolicy) {
			return null;
		}

		return <EditSharingMenu sharingPolicy={sharingPolicy} />;
	}
}
